import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { DateRangePickerPreset, DateRangePickerPresetId, generatePresets } from './date-range-preset';

/** Custom `MatFormFieldControl` for date selection. */
@Component({
    selector: 'tm-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    host: {
        'class': 'tm-date-range-picker'
    },
    imports: [
        CommonModule,
        FormsModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        OverlayModule,
        ReactiveFormsModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TmDateRangePickerComponent),
            multi: true
        }
    ]
})
export class TmDateRangePickerComponent implements ControlValueAccessor {
  private _value?: DateRange<Date>;
  private _presetIds: DateRangePickerPresetId[];
  
  protected startCtrl = new FormControl<Date>(null);
  protected endCtrl = new FormControl<Date>(null);

  protected presets: (DateRangePickerPreset<Date> & Selectable)[] = [];
  protected isOpen = false;
  protected calendarOpened = false;
  protected isDisabled = false;

  protected get value() {
    return this._value;
  }

  protected set value(value) {
    this._value = value;
    this._propagateSelectedDateRange();
    this.onChange(value);
    this.onTouched();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  @Input()
  get presetIds(): DateRangePickerPresetId[] {
    return this._presetIds;
  }

  set presetIds(presetIds: DateRangePickerPresetId[]) {
    this._presetIds = presetIds;
    this.presets = generatePresets(presetIds, "Europe/Paris");
    this._propagateSelectedDateRange();
  }

  @Input()
  maxDate: Date;

  constructor(
    private _dateAdapter: DateAdapter<Date>,
  ) {
  }
  
  writeValue(value: DateRange<Date>): void {
    this._value = value;

    this.startCtrl.setValue(value?.start);
    this.endCtrl.setValue(value?.end);
    this._propagateSelectedDateRange();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.startCtrl.disable();
      this.endCtrl.disable();
    } else {
      this.startCtrl.enable();
      this.endCtrl.enable();
    }
  }

  selectPreset(preset: DateRangePickerPreset<Date>) {
    const value = preset.range;

    this.startCtrl.setValue(value.start);
    this.endCtrl.setValue(value.end);
    this.value = value;
    this.isOpen = false;
  }

  dateRangeChanged() {
    
    if (this.startCtrl.value && this.endCtrl.value) {
      this.value = new DateRange<Date>(this.startCtrl.value, this.endCtrl.value);
    } else {
      this.startCtrl.setValue(this.value?.start);
      this.endCtrl.setValue(this.value?.end);
    }

    this.calendarOpened = false;  
  }

  private _propagateSelectedDateRange() {
    this.presets = this.presets.map(elem => ({
      ...elem,
      isSelected: this._isSelected(elem.range)
    }))
  }

  private _isSelected(dateRange: DateRange<Date>) {
    if (!this.value) {
      return false;
    }

    return this._dateAdapter.sameDate(dateRange.start, this.value?.start) && this._dateAdapter.sameDate(dateRange.end, this.value?.end);
  }
}

interface Selectable {
  isSelected?: boolean;
}